import type { JSX } from "react";
import { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useNavigate, useOutletContext } from "react-router-dom";
import { fetchHealth } from "../lib/api";
import { getUser, clearToken } from "../lib/auth";
import { daemonStatus, daemonRevoke, type DaemonDeviceView } from "../lib/meeting-api";
import { preferredMicId, setPreferredMicId } from "../lib/mic";
import { BrandLogo } from "../components/BrandLogo";
import { IconHeadset, IconArrowRight } from "../components/icons";

/**
 * /settings — the AI-native audit surface (openspec/settings-surface.md).
 * NOT a configuration menu: it shows what 小环 currently decides on the
 * user's behalf; the primary path for configuration stays in-flow (小环 asks
 * when a decision is actually needed). Controls appear only where the user
 * genuinely owns the decision (their devices, their microphone).
 *
 * Shell reuses the /admin cc-rail + cc-main grammar (console.css); the
 * grouped-card row language (label + effective value + fine print, Apple
 * Settings 式) lives in settings.css. Dark (FEATURE_SETTINGS off) → redirect
 * home, no entry anywhere.
 */

interface SettingsFlags {
  systemAudio: boolean;
  privacy: boolean;
}

export function SettingsView(): JSX.Element {
  const navigate = useNavigate();
  const user = getUser();
  // null = still checking; false = flag off / unauthenticated / server down.
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [flags, setFlags] = useState<SettingsFlags>({ systemAudio: false, privacy: false });

  useEffect(() => {
    void fetchHealth().then((r) => {
      if (r.kind !== "ok" || r.health.features?.settings !== true) {
        setAllowed(false);
        return;
      }
      if (r.health.authEnabled && getUser() === null) {
        setAllowed(false);
        return;
      }
      setFlags({
        systemAudio: r.health.features?.meetingSystemAudio === true,
        privacy: r.health.features?.meetingPrivacy === true
      });
      setAllowed(true);
    });
  }, []);

  if (allowed === false) return <Navigate to="/" replace />;
  if (allowed === null) return <div className="cc-shell st-shell" aria-busy="true" />;

  return (
    <div className="cc-shell st-shell">
      <nav className="cc-rail">
        <div className="cc-brand">
          <BrandLogo size={22} />
          <span className="cc-brand-name">设置</span>
        </div>
        <button className="cc-back" type="button" onClick={() => navigate("/")}>
          <IconArrowRight size={14} style={{ transform: "rotate(180deg)" }} />
          返回对话
        </button>
        <div className="st-rail-label">能力</div>
        <div className="cc-rail-nav">
          <NavLink
            to="/settings/meetings"
            className={({ isActive }) => `cc-rail-link${isActive ? " active" : ""}`}
          >
            <IconHeadset size={14} />
            会议
          </NavLink>
        </div>
        <span className="cc-rail-spacer" />
        {user !== null && (
          <div className="cc-rail-account">
            <span className="cc-rail-account-name" title={`${user.username} · ${user.tenant}`}>
              {user.username}
              <span className="cc-rail-account-tenant"> · {user.tenant}</span>
            </span>
            <button
              className="cc-rail-logout"
              type="button"
              onClick={() => {
                clearToken();
                navigate("/");
              }}
            >
              退出
            </button>
          </div>
        )}
      </nav>
      <div className="cc-main st-main">
        <Outlet context={flags} />
      </div>
    </div>
  );
}

/** Meetings section — audit rows first; controls only where the user owns the
 *  decision (device revoke, microphone pick). */
export function MeetingsSettings(): JSX.Element {
  const flags = useOutletContext<SettingsFlags | null>() ?? { systemAudio: false, privacy: false };

  const [devices, setDevices] = useState<DaemonDeviceView[]>([]);
  useEffect(() => {
    if (!flags.systemAudio) return;
    daemonStatus().then((s) => setDevices(s.devices)).catch(() => setDevices([]));
  }, [flags.systemAudio]);
  const revoke = async (id: string): Promise<void> => {
    try {
      await daemonRevoke(id);
      setDevices((ds) => ds.filter((d) => d.id !== id));
    } catch {
      /* keep the row; the next visit reflects reality */
    }
  };

  const [mics, setMics] = useState<{ id: string; label: string }[]>([]);
  const [micChoice, setMicChoice] = useState<string>(preferredMicId());
  useEffect(() => {
    navigator.mediaDevices?.enumerateDevices?.()
      .then((ds) =>
        setMics(
          ds.filter((d) => d.kind === "audioinput" && d.deviceId !== "default" && d.deviceId !== "communications" && d.label.length > 0)
            .map((d) => ({ id: d.deviceId, label: d.label }))
        )
      )
      .catch(() => setMics([]));
  }, []);

  const onlineDevice = devices.some((d) => d.online);

  return (
    <div className="st-page">
      <header className="st-head">
        <h1 className="st-title">会议</h1>
        <p className="st-sub">录下会议，拿回可执行的纪要。</p>
        <p className="st-ai-note">
          这一页是审计与覆盖——用不用它都不影响功能：需要你做决定时，小环会在用到的当下问你。
        </p>
      </header>

      <section className="st-group">
        <div className="st-group-title">录制方式</div>
        <div className="st-row">
          <div className="st-row-main">
            <div className="st-row-label">录制入口</div>
            <div className="st-row-desc">由小环按场景分流，无需选择模式</div>
          </div>
          <div className="st-row-value">
            {flags.systemAudio && onlineDevice ? "先问场景（桌面端在线）" : "浏览器麦克风"}
          </div>
        </div>
        <div className="st-foot">
          有在线的桌面采集设备时，点录制小环会先问「这场会在哪儿？」——线上会议用桌面端（能录到对方的声音），现场对话用浏览器麦克风；没有设备时直接用浏览器录。
        </div>
      </section>

      {flags.systemAudio && (
        <section className="st-group">
          <div className="st-group-title">桌面采集设备</div>
          {devices.length === 0 ? (
            <div className="st-row">
              <div className="st-row-main">
                <div className="st-row-label st-muted">还没有连接的设备</div>
              </div>
            </div>
          ) : (
            devices.map((d) => (
              <div className="st-row" key={d.id}>
                <div className="st-row-main">
                  <div className="st-row-label">
                    <span className={`st-dot${d.online ? " on" : ""}`} aria-hidden="true" />
                    {d.name}
                  </div>
                  <div className="st-row-desc">{d.online ? "在线" : "离线"}</div>
                </div>
                <button className="st-revoke" type="button" onClick={() => void revoke(d.id)}>
                  解绑
                </button>
              </div>
            ))
          )}
          <div className="st-foot">
            配对在会议页完成：点「连接桌面采集」，小环会唤起桌面端自动配好。解绑立即撤销该设备的录制权限。
          </div>
        </section>
      )}

      <section className="st-group">
        <div className="st-group-title">麦克风</div>
        <div className="st-row">
          <div className="st-row-main">
            <div className="st-row-label">浏览器录音输入</div>
            <div className="st-row-desc">桌面端录制用系统输入设备，不受此项影响</div>
          </div>
          {mics.length > 0 ? (
            <select
              className="st-select"
              value={micChoice}
              onChange={(e) => {
                setMicChoice(e.target.value);
                setPreferredMicId(e.target.value);
              }}
            >
              <option value="">系统默认</option>
              {mics.map((d) => (
                <option key={d.id} value={d.id}>{d.label}</option>
              ))}
            </select>
          ) : (
            <div className="st-row-value">系统默认</div>
          )}
        </div>
        <div className="st-foot">首次录音授权麦克风后，这里可以改选指定设备。</div>
      </section>

      <section className="st-group st-privacy">
        <div className="st-group-title">隐私</div>
        <p className="st-privacy-text">
          录音只用于生成你的转写与纪要，处理后音频不落盘存储；小环不会用你的录音或转写训练模型。
          {flags.privacy && "你的租户若启用「不出内网」私密档，音频只在你所在的网络内完成转写。"}
        </p>
      </section>
    </div>
  );
}
